import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { myApi } from "../../config/axiosConfig";
import { useNavigate } from "react-router-dom";

const PERIODS = ["Year", "Month", "Week", "Day"];
const MONTH_ORDER = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};
const MX_HUBS = ["EBT Colombia", "Patio EBT MTY"];

const US_HUBS = [
  "EBT Tower",
  "EBT Torre",
  "Loves Laredo,TX",
  "LovesLaredo,TX"
];

const n0 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const n1 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function parseDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatHoursMinutes(totalSeconds) {
  const seconds = Math.max(0, Math.round(toNumber(totalSeconds)));
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${pad2(hrs)}:${pad2(mins)} Hrs.`;
}

function formatDurationLong(totalSeconds) {
  const seconds = Math.max(0, Math.round(toNumber(totalSeconds)));
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hrs}:${pad2(mins)} Hrs.`;
}

function getWeekNumber(date) {
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
}

function getPeriodMeta(date, period) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const week = getWeekNumber(date);

  if (period === "Year") {
    return {
      key: `${year}`,
      label: `${year}`,
      sortValue: new Date(year, 0, 1).getTime(),
    };
  }

  if (period === "Month") {
    const label = date.toLocaleString("en-US", { month: "long" });
    return {
      key: `${year}-${pad2(month)}`,
      label,
      sortValue: new Date(year, month - 1, 1).getTime(),
    };
  }

  if (period === "Week") {
    return {
      key: `${year}-W${pad2(week)}`,
      label: `${week}`,
      sortValue: new Date(year, 0, 1 + (week - 1) * 7).getTime(),
    };
  }

  return {
    key: `${year}-${pad2(month)}-${pad2(day)}`,
    label: `${pad2(month)}/${pad2(day)}`,
    sortValue: new Date(year, month - 1, day).getTime(),
  };
}

function getRowGeo(row) {
  const hub = String(row?.hub_name || "").trim();

  if (MX_HUBS.includes(hub)) return "MX";
  if (US_HUBS.includes(hub)) return "US";

  return "US";
}

function calculateIdleCostUSD(row, dieselMX, dieselUS, exchangeRate) {
  const fuelGallons = toNumber(row?.fuel_consumed);
  const geo = getRowGeo(row);

  if (geo === "MX") {
    const liters = fuelGallons * 3.78541;
    const mxn = liters * toNumber(dieselMX);
    return exchangeRate > 0 ? mxn / exchangeRate : 0;
  }

  return fuelGallons * toNumber(dieselUS);
}

function getDurationColor(seconds) {
  const hrs = toNumber(seconds) / 3600;
  if (hrs >= 1.5) return "#ff1e1e";
  if (hrs >= 1.0) return "#ff8a65";
  if (hrs >= 0.65) return "#fbc02d";
  if (hrs >= 0.4) return "#d4e157";
  return "#18ff2a";
}

function getCostColor(cost) {
  const n = toNumber(cost);
  if (n >= 3000) return "#00ff57";
  if (n >= 1800) return "#8cff00";
  if (n >= 900) return "#ffd400";
  if (n >= 400) return "#ffb74d";
  return "#76ff7a";
}

function buildSummary(rows, dieselMX, dieselUS, exchangeRate) {
  const incidents = rows.reduce((acc, row) => acc + toNumber(row.incidents), 0);
  const totalSeconds = rows.reduce((acc, row) => acc + toNumber(row.totalDurationSec), 0);
  const totalFuel = rows.reduce((acc, row) => acc + toNumber(row.totalFuel), 0);

  const totalCost = rows.reduce((acc, row) => {
    const mxFuel = toNumber(row.mxFuel);
    const usFuel = toNumber(row.usFuel);

    const mxCost =
      exchangeRate > 0
        ? (mxFuel * toNumber(dieselMX)) / exchangeRate
        : 0;

    const usCost = usFuel * dieselUS;

    return acc + mxCost + usCost;
  }, 0);

  return {
    incidents,
    totalSeconds,
    avgSeconds: incidents > 0 ? totalSeconds / incidents : 0,
    totalFuel,
    totalCost,
  };
}

function buildChartData(rows) {
  return rows.map((row) => ({
    label: row.label,
    incidents: toNumber(row.incidents),
    avgHours: Number((toNumber(row.avgDurationSec) / 3600).toFixed(2)),
    totalFuel: toNumber(row.totalFuel),
  }));
}

function buildPeriodTable(rows, dieselMX, dieselUS, exchangeRate) {
  const rowsOut = rows.map((row, index) => {
    const fuelGallons = toNumber(row.totalFuel);
    const mxFuel = toNumber(row.mxFuel);
    const usFuel = toNumber(row.usFuel);

    const mxCost =
      exchangeRate > 0
        ? (mxFuel * toNumber(dieselMX)) / exchangeRate
        : 0;

    const usCost = usFuel * toNumber(dieselUS);

    let totalCost = mxCost + usCost;

    return {
      key: `${row.label}-${index}`,
      label: row.label,
      sortValue: row.sortValue,
      incidents: toNumber(row.incidents),
      avgSeconds: toNumber(row.avgDurationSec),
      totalSeconds: toNumber(row.totalDurationSec),
      totalFuel: fuelGallons,
      totalCost: Number(totalCost.toFixed(2)),
      approxPpg: fuelGallons > 0 ? Number((totalCost / fuelGallons).toFixed(2)) : 0,
    };
  });

  const totals = rowsOut.reduce(
    (acc, row) => {
      acc.incidents += row.incidents;
      acc.totalSeconds += row.totalSeconds;
      acc.totalFuel += row.totalFuel;
      acc.totalCost += row.totalCost;
      return acc;
    },
    { incidents: 0, totalSeconds: 0, totalFuel: 0, totalCost: 0 }
  );

  return {
    rows: rowsOut,
    total: {
      incidents: totals.incidents,
      avgSeconds: totals.incidents > 0 ? totals.totalSeconds / totals.incidents : 0,
      totalSeconds: totals.totalSeconds,
      totalFuel: Number(totals.totalFuel.toFixed(1)),
      totalCost: Number(totals.totalCost.toFixed(2)),
      approxPpg: totals.totalFuel > 0 ? Number((totals.totalCost / totals.totalFuel).toFixed(2)) : 0,
    },
  };
}

function buildLocationTable(rows, dieselMX, dieselUS, exchangeRate) {
  const grouped = {};

  rows.forEach((row) => {
    const key = String(row?.hub_name || "Unknown").trim() || "Unknown";

    if (!grouped[key]) {
      grouped[key] = {
        location: key,
        incidents: 0,
        totalSeconds: 0,
        totalFuel: 0,
        totalCost: 0,
      };
    }

    const fuelGallons = toNumber(row.totalFuel);
    const hub = key.trim();

    let cost = 0;
    if (MX_HUBS.includes(hub)) {
      const liters = fuelGallons * 3.78541;
      const mxn = liters * toNumber(dieselMX);
      cost = exchangeRate > 0 ? mxn / exchangeRate : 0;
    } else {
      cost = fuelGallons * toNumber(dieselUS);
    }

    grouped[key].incidents += toNumber(row.incidents);
    grouped[key].totalSeconds += toNumber(row.totalDurationSec);
    grouped[key].totalFuel += fuelGallons;
    grouped[key].totalCost += cost;
  });

  const rowsOut = Object.values(grouped)
    .map((item) => {
      const totalFuel = Number(item.totalFuel.toFixed(1));
      const totalCost = Number(item.totalCost.toFixed(2));

      return {
        ...item,
        avgSeconds: item.incidents > 0 ? item.totalSeconds / item.incidents : 0,
        totalFuel,
        totalCost,
        approxPpg: totalFuel > 0 ? Number((totalCost / totalFuel).toFixed(2)) : 0,
      };
    })
    .sort((a, b) => b.totalCost - a.totalCost);

  const total = rowsOut.reduce(
    (acc, row) => {
      acc.incidents += row.incidents;
      acc.totalSeconds += row.totalSeconds;
      acc.totalFuel += row.totalFuel;
      acc.totalCost += row.totalCost;
      return acc;
    },
    { incidents: 0, totalSeconds: 0, totalFuel: 0, totalCost: 0 }
  );

  return {
    rows: rowsOut,
    total: {
      incidents: total.incidents,
      avgSeconds: total.incidents > 0 ? total.totalSeconds / total.incidents : 0,
      totalSeconds: total.totalSeconds,
      totalFuel: Number(total.totalFuel.toFixed(1)),
      totalCost: Number(total.totalCost.toFixed(2)),
      approxPpg: total.totalFuel > 0
      ? Number((total.totalCost / total.totalFuel).toFixed(2))
      : 0,
    },
  };
}

function buildTruckTable(rows, dieselMX, dieselUS, exchangeRate) {
  const rowsOut = rows.map((row) => {
    const truck = String(row?.vehicle_tag || "Unknown").trim() || "Unknown";
    const fuelGallons = toNumber(row.totalFuel);

    const mxFuel = toNumber(row.mxFuel);
    const usFuel = toNumber(row.usFuel);

    const mxCost =
      exchangeRate > 0
        ? (mxFuel * toNumber(dieselMX)) / exchangeRate
        : 0;

    const usCost = usFuel * toNumber(dieselUS);

    const totalCost = mxCost + usCost;

    const totalFuel = Number(fuelGallons.toFixed(1));
    const roundedTotalCost = Number(totalCost.toFixed(2));

    return {
      truck,
      incidents: toNumber(row.incidents),
      avgSeconds: toNumber(row.avgDurationSec),
      totalSeconds: toNumber(row.totalDurationSec),
      totalFuel,
      totalCost: roundedTotalCost,
      approxPpg: totalFuel > 0
        ? Number((roundedTotalCost / totalFuel).toFixed(2))
        : 0,
    };
  });

  const total = rowsOut.reduce(
    (acc, row) => {
      acc.incidents += row.incidents;
      acc.totalSeconds += row.totalSeconds;
      acc.totalFuel += row.totalFuel;
      acc.totalCost += row.totalCost;
      return acc;
    },
    { incidents: 0, totalSeconds: 0, totalFuel: 0, totalCost: 0 }
  );

  return {
    rows: rowsOut,
    total: {
      incidents: total.incidents,
      avgSeconds: total.incidents > 0 ? total.totalSeconds / total.incidents : 0,
      totalSeconds: total.totalSeconds,
      totalFuel: Number(total.totalFuel.toFixed(1)),
      totalCost: Number(total.totalCost.toFixed(2)),
      approxPpg: total.totalFuel > 0
        ? Number((total.totalCost / total.totalFuel).toFixed(2))
        : 0,
    },
  };
}

function KpiCard({ title, value, subtitle, minHeight = 110 }) {
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #d8d8d8",
        minHeight,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        bgcolor: "#f7f7f7",
      }}
    >
      <Typography sx={{ fontSize: 16, color: "#333", mb: 0.5, fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 34, lineHeight: 1, fontWeight: 900, color: "#1f1f1f" }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography sx={{ fontSize: 12, color: "#666", mt: 0.75 }}>{subtitle}</Typography>
      ) : null}
    </Paper>
  );
}

function HeaderCell({ children, align = "left", onClick, active, direction }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1,
        py: 0.9,
        bgcolor: "#2f2f2f",
        color: "#fff",
        fontWeight: 800,
        fontSize: 14,
        borderBottom: "2px solid #3b9cff",
        textAlign: align,
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        zIndex: 2,
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
      }}
    >
      {children} {active ? (direction === "asc" ? "▲" : "▼") : ""}
    </Box>
  );
}

function Cell({ children, align = "left", bg = "#f3f3f3", bold = false, color = "#222" }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 0.72,
        fontSize: 14,
        fontWeight: bold ? 800 : 500,
        textAlign: align,
        bgcolor: bg,
        color,
        borderBottom: "1px solid #d7d7d7",
        borderRight: "1px solid #d7d7d7",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

export default function MasterDashboardIdling() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const [loading, setLoading] = useState(false);
  const [loadingCosts, setLoadingCosts] = useState(true);
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState("Day");
  const [year, setYear] = useState(currentYear);
  const [location, setLocation] = useState("All");
  const [dieselMX, setDieselMX] = useState(0);
  const [dieselUS, setDieselUS] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(0);

  const [periodSort, setPeriodSort] = useState({ field: "label", direction: "desc" });
  const [locationSort, setLocationSort] = useState({ field: "totalCost", direction: "desc" });
  const [truckSort, setTruckSort] = useState({ field: "totalCost", direction: "desc" });

  const [countryFilter, setCountryFilter] = useState("MEX");

  const [filters, setFilters] = useState({
    periodLabel: null,
    location: null,
    truck: null,
  });

  const toggleFilter = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: prev[field] === value ? null : value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      periodLabel: null,
      location: null,
      truck: null,
    });
  };

  const handleSort = (setter, field) => {
    setter((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

const sortRows = (rows, sort) => {
  return [...rows].sort((a, b) => {
    if (period === "Month" && sort.field === "label") {
      const aMonth = MONTH_ORDER[a.label] || 0;
      const bMonth = MONTH_ORDER[b.label] || 0;

      return sort.direction === "asc"
        ? aMonth - bMonth
        : bMonth - aMonth;
    }

    const aVal = a[sort.field];
    const bVal = b[sort.field];

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sort.direction === "asc"
        ? aVal - bVal
        : bVal - aVal;
    }

    return sort.direction === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });
};

  const getDiesel = async () => {
  const res = await myApi.get("/cargasFuel/getDieselPriceMX");
  return toNumber(res?.data?.price);
};

const getDieselUS = async () => {
  const res = await myApi.get("/cargasFuel/getDieselPriceUS");
  return toNumber(res?.data?.price);
};

const getRate = async () => {
  const res = await myApi.get("/cargasFuel/getExchangeRateMX");
  return toNumber(res?.data?.rate);
};

const [yearsList, setYearsList] = useState([]);
const [periodRows, setPeriodRows] = useState([]);
const [locationRows, setLocationRows] = useState([]);
const [truckRows, setTruckRows] = useState([]);

  const fetchIdling = async () => {
  setLoading(true);

  try {
    const response = await myApi.post("/cargasFuel/getDashIdling", {
      year,
      location: location === "All" ? "All" : location,
      period,

      countryFilter,

      filterPeriodLabel: filters.periodLabel,
      filterLocation: filters.location,
      filterTruck: filters.truck,
    });

    console.log("IDLING RESPONSE:", response.data);

    if (response?.data?.success) {
      setYearsList(Array.isArray(response.data.years) ? response.data.years : []);
      setPeriodRows(Array.isArray(response.data.periodData) ? response.data.periodData : []);
      setLocationRows(Array.isArray(response.data.locationData) ? response.data.locationData : []);
      setTruckRows(Array.isArray(response.data.truckData) ? response.data.truckData : []);
    } else {
      setYearsList([]);
      setPeriodRows([]);
      setLocationRows([]);
      setTruckRows([]);
    }
  } catch (error) {
    console.error("Error fetching idling data:", error);
    setYearsList([]);
    setPeriodRows([]);
    setLocationRows([]);
    setTruckRows([]);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchIdling();
  }, [year, location, period, filters, countryFilter]);

  useEffect(() => {
  const loadCosts = async () => {
    setLoadingCosts(true);

    try {
      const [mx, us, rate] = await Promise.all([
        getDiesel(),
        getDieselUS(),
        getRate(),
      ]);

      setDieselMX(mx);
      setDieselUS(us);
      setExchangeRate(rate);
    } catch (error) {
      console.error("Error loading costs:", error);
    } finally {
      setLoadingCosts(false);
    }
  };

  loadCosts();
}, []);

  const years = useMemo(() => {
  const list = yearsList
    .map((row) => Number(row.year))
    .filter(Boolean)
    .sort((a, b) => b - a);

  return list.length ? list : [2026, 2025];
}, [yearsList]);

 const locations = useMemo(() => {
  return [
    "All",
    "EBT Colombia",
    "EBT Tower",
    "Loves Laredo,TX",
    "Patio EBT MTY"
  ];
}, []);

  const filteredRows = useMemo(() => periodRows, [periodRows]);

  const summary = useMemo(
  () => buildSummary(periodRows, dieselMX, dieselUS, exchangeRate),
  [periodRows, dieselMX, dieselUS, exchangeRate]
);

  const chartData = useMemo(() => buildChartData(periodRows), [periodRows]);
  const periodTable = useMemo(
  () => buildPeriodTable(periodRows, dieselMX, dieselUS, exchangeRate),
  [periodRows, dieselMX, dieselUS, exchangeRate]
);
  const locationTable = useMemo(
  () => buildLocationTable(locationRows, dieselMX, dieselUS, exchangeRate),
  [locationRows, dieselMX, dieselUS, exchangeRate]
);
  const truckTable = useMemo(
  () => buildTruckTable(truckRows, dieselMX, dieselUS, exchangeRate),
  [truckRows, dieselMX, dieselUS, exchangeRate]
);

const globalLoading = loading || loadingCosts;

const sortedPeriodRows = useMemo(
  () => sortRows(periodTable.rows, periodSort),
  [periodTable.rows, periodSort]
);

const sortedLocationRows = useMemo(
  () => sortRows(locationTable.rows, locationSort),
  [locationTable.rows, locationSort]
);

const sortedTruckRows = useMemo(
  () => sortRows(truckTable.rows, truckSort),
  [truckTable.rows, truckSort]
);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#ececec", p: 1 }}>
      <Paper
        sx={{
          height: "calc(100vh - 180px)",
          bgcolor: "#ececec",
          borderRadius: 0,
          border: "1px solid #dadada",
          p: 1,
          overflow: "hidden",
        }}
      >
        {globalLoading  ? (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "grid",
              gridTemplateRows: "auto auto 1fr",
              gap: 1,
              overflow: "hidden",
            }}
          >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "180px 240px 1fr auto",
                  gap: 2,
                  alignItems: "end",
                }}
              >
              <TextField
                select
                label="Year"
                size="small"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Location"
                size="small"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                {locations.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>

              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-start"
                flexWrap="wrap"
              >
                {PERIODS.map((item) => (
                  <Button
                    key={item}
                    variant={period === item ? "contained" : "outlined"}
                    onClick={() => setPeriod(item)}
                    sx={{
                      minWidth: 100,
                      height: 40,
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 800,
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ToggleButtonGroup
                  value={countryFilter}
                  exclusive
                  onChange={(e, value) => {
                    if (value !== null) {
                      setCountryFilter(value);
                      clearFilters();
                    }
                  }}
                  size="small"
                  sx={{
                    mr: 1,
                    "& .MuiToggleButton-root": {
                      px: 2,
                      fontWeight: 800,
                      textTransform: "none",
                      borderRadius: "8px !important",
                    },
                  }}
                >
                  <ToggleButton value="ALL">ALL</ToggleButton>
                  <ToggleButton value="MEX">MEX</ToggleButton>
                  <ToggleButton value="USA">USA</ToggleButton>
                </ToggleButtonGroup>

                <Button
                  variant="contained"
                  onClick={() => navigate("/diesel/kpi/dashboard")}
                  sx={{
                    minWidth: 96,
                    height: 40,
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 800,
                    boxShadow: "0 3px 10px rgba(0,0,0,0.18)",
                  }}
                >
                  MAIN
                </Button>

                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{
                    minWidth: 120,
                    height: 40,
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 800,
                    mr: 1,
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(140px, 1fr))",
                gap: 1,
              }}
            >
              <KpiCard title="Exchange Rate" value={`$${n2.format(exchangeRate)}`} />
              <KpiCard title="USA PPG (EIA)" value={`$${n2.format(dieselUS)}`} />
              <KpiCard title="MX PPL" value={`$${n2.format(dieselMX)}`} />
              <KpiCard title="# Incidents" value={n0.format(summary.incidents)} />
              <KpiCard title="Avg Duration" value={formatHoursMinutes(summary.avgSeconds)} />
              <KpiCard title="Total Duration" value={formatDurationLong(summary.totalSeconds)} />
              <KpiCard
                title="Approx Idle Cost"
                value={
                    loadingCosts ? (
                    <Box
                        sx={{
                        width: 120,
                        height: 38,
                        borderRadius: 1,
                        bgcolor: "#dcdcdc",
                        animation: "pulse 1.2s infinite",
                        "@keyframes pulse": {
                            "0%": { opacity: 0.55 },
                            "50%": { opacity: 1 },
                            "100%": { opacity: 0.55 },
                        },
                        }}
                    />
                    ) : (
                    money.format(summary.totalCost)
                    )
                }
                subtitle={
                    loadingCosts
                    ? "Calculating..."
                    : `${n1.format(summary.totalFuel)} g total fuel`
                }
                />
            </Box>

            <Box
              sx={{
                minHeight: 0,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.65fr) minmax(0, 1fr)",
                gap: 1,
                overflow: "hidden",
              }}
            >
              {/* <Box sx={{ minWidth: 0, minHeight: 0, display: "grid", gridTemplateRows: "300px 1fr", gap: 1 }}> */}
              <Box sx={{ minWidth: 0, minHeight: 0, display: "grid", gridTemplateRows: "1fr", gap: 1 }}>
{/*                 <Paper
                sx={{
                    borderRadius: 0,
                    border: "1px solid #d5d5d5",
                    bgcolor: "#f5f5f5",
                    p: 1,
                    overflow: "hidden",
                }}
                >
                <Typography sx={{ fontWeight: 800, mb: 1 }}>
                    Idle Cost Analysis ({period})
                </Typography>

                <Box
                    sx={{
                    width: "100%",
                    height: "90%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    }}
                >
                    <Box
                    sx={{
                        height: "100%",
                        minWidth:
                        period === "Day"
                            ? Math.max(chartData.length * 42, 1400)
                            : "100%",
                    }}
                    >
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                        data={chartData}
                        margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                        >
                        <CartesianGrid stroke="#d0d0d0" strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="left" dataKey="incidents" name="# Incidents" />
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="avgHours"
                            name="Avg Hours"
                            strokeWidth={3}
                        />
                        </LineChart>
                    </ResponsiveContainer>
                    </Box>
                </Box>
                </Paper> */}

                <Paper sx={{ borderRadius: 0, border: "1px solid #d5d5d5", bgcolor: "#f4f4f4", minHeight: 0, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", overflow: "auto" }}>
                    <Box sx={{ display: "grid", 
/*                       gridTemplateColumns: "160px 100px 110px 120px 110px 180px", 
                      minWidth: 780  */
                      gridTemplateColumns: "160px 100px 110px 120px 110px 180px 120px",
                      minWidth: 900
                      }}>
                      <HeaderCell
                        onClick={() => handleSort(setPeriodSort, "label")}
                        active={periodSort.field === "label"}
                        direction={periodSort.direction}
                      >
                        {period}
                      </HeaderCell>

                      <HeaderCell
                        align="center"
                        onClick={() => handleSort(setPeriodSort, "incidents")}
                        active={periodSort.field === "incidents"}
                        direction={periodSort.direction}
                      >
                        #Incidents
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setPeriodSort, "avgSeconds")}
                        active={periodSort.field === "avgSeconds"}
                        direction={periodSort.direction}
                      >
                        Avg Duration
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setPeriodSort, "totalSeconds")}
                        active={periodSort.field === "totalSeconds"}
                        direction={periodSort.direction}
                      >
                        Total Duration
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setPeriodSort, "totalFuel")}
                        active={periodSort.field === "totalFuel"}
                        direction={periodSort.direction}
                      >
                        Total Fuel
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setPeriodSort, "totalCost")}
                        active={periodSort.field === "totalCost"}
                        direction={periodSort.direction}
                      >
                        Approx Idle Cost
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setPeriodSort, "approxPpg")}
                        active={periodSort.field === "approxPpg"}
                        direction={periodSort.direction}
                      >
                        Approx PPG
                      </HeaderCell>

                      {sortedPeriodRows.map((row) => (
                        <React.Fragment key={row.key}>
                          <Cell
                            bg={filters.periodLabel === row.label ? "#bbdefb" : "#f3f3f3"}
                          >
                            <Box
                              onClick={() => toggleFilter("periodLabel", row.label)}
                              sx={{ cursor: "pointer", fontWeight: 800 }}
                            >
                              {row.label}
                            </Box>
                          </Cell>
                          <Cell align="center" bold>{n0.format(row.incidents)}</Cell>
                          <Cell bg={getDurationColor(row.avgSeconds)}>{formatHoursMinutes(row.avgSeconds)}</Cell>
                          <Cell bold>{formatDurationLong(row.totalSeconds)}</Cell>
                          <Cell>{n1.format(row.totalFuel)} g</Cell>
                          <Cell bg={getCostColor(row.totalCost)}>{money.format(row.totalCost)}</Cell>
                          <Cell>{money.format(row.approxPpg)}</Cell>
                        </React.Fragment>
                      ))}

                      <Cell bold>Total</Cell>
                      <Cell align="center" bold>{n0.format(periodTable.total.incidents)}</Cell>
                      <Cell bg={getDurationColor(periodTable.total.avgSeconds)} bold>{formatHoursMinutes(periodTable.total.avgSeconds)}</Cell>
                      <Cell bold>{formatDurationLong(periodTable.total.totalSeconds)}</Cell>
                      <Cell bold>{n1.format(periodTable.total.totalFuel)} g</Cell>
                      <Cell bg={getCostColor(periodTable.total.totalCost)} bold>{money.format(periodTable.total.totalCost)}</Cell>
                      <Cell bold>{money.format(periodTable.total.approxPpg)}</Cell>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              <Box sx={{ minWidth: 0, minHeight: 0, display: "grid", gridTemplateRows: "1fr 1fr", gap: 1 }}>
                <Paper sx={{ borderRadius: 0, border: "1px solid #d5d5d5", bgcolor: "#f4f4f4", minHeight: 0, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", overflow: "auto" }}>
                    <Box sx={{ display: "grid", 
/*                       gridTemplateColumns: "1.35fr 0.7fr 0.9fr 1fr 0.9fr 1fr", 
                      minWidth: 760  */

                      gridTemplateColumns: "1.35fr 0.7fr 0.9fr 1fr 0.9fr 1fr 0.9fr",
                      minWidth: 880
                      
                      }}>
                      <HeaderCell
                        onClick={() => handleSort(setLocationSort, "location")}
                        active={locationSort.field === "location"}
                        direction={locationSort.direction}
                      >
                        Location
                      </HeaderCell>

                      <HeaderCell
                        align="center"
                        onClick={() => handleSort(setLocationSort, "incidents")}
                        active={locationSort.field === "incidents"}
                        direction={locationSort.direction}
                      >
                        #Incidents
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setLocationSort, "avgSeconds")}
                        active={locationSort.field === "avgSeconds"}
                        direction={locationSort.direction}
                      >
                        Avg Duration
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setLocationSort, "totalSeconds")}
                        active={locationSort.field === "totalSeconds"}
                        direction={locationSort.direction}
                      >
                        Total Duration
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setLocationSort, "totalFuel")}
                        active={locationSort.field === "totalFuel"}
                        direction={locationSort.direction}
                      >
                        Total Fuel
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setLocationSort, "totalCost")}
                        active={locationSort.field === "totalCost"}
                        direction={locationSort.direction}
                      >
                        Approx Idle Cost
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setLocationSort, "approxPpg")}
                        active={locationSort.field === "approxPpg"}
                        direction={locationSort.direction}
                      >
                        Approx PPG
                      </HeaderCell>

                      {sortedLocationRows.map((row) => (
                        <React.Fragment key={row.location}>
                          <Cell
                            bg={filters.location === row.location ? "#bbdefb" : "#f3f3f3"}
                          >
                            <Box
                              onClick={() => toggleFilter("location", row.location)}
                              sx={{ cursor: "pointer", fontWeight: 800 }}
                            >
                              {row.location}
                            </Box>
                          </Cell>
                          <Cell align="center">{n0.format(row.incidents)}</Cell>
                          <Cell bg={getDurationColor(row.avgSeconds)}>{formatHoursMinutes(row.avgSeconds)}</Cell>
                          <Cell>{formatDurationLong(row.totalSeconds)}</Cell>
                          <Cell>{n1.format(row.totalFuel)} g</Cell>
                          <Cell bg={getCostColor(row.totalCost)}>{money.format(row.totalCost)}</Cell>
                          <Cell>{money.format(row.approxPpg)}</Cell>
                        </React.Fragment>
                      ))}

                      <Cell bold>Total</Cell>
                      <Cell align="center" bold>{n0.format(locationTable.total.incidents)}</Cell>
                      <Cell bg={getDurationColor(locationTable.total.avgSeconds)} bold>{formatHoursMinutes(locationTable.total.avgSeconds)}</Cell>
                      <Cell bold>{formatDurationLong(locationTable.total.totalSeconds)}</Cell>
                      <Cell bold>{n1.format(locationTable.total.totalFuel)} g</Cell>
                      <Cell bg={getCostColor(locationTable.total.totalCost)} bold>{money.format(locationTable.total.totalCost)}</Cell>
                      <Cell bold>{money.format(locationTable.total.approxPpg)}</Cell>
                    </Box>
                  </Box>
                </Paper>

                <Paper sx={{ borderRadius: 0, border: "1px solid #d5d5d5", bgcolor: "#f4f4f4", minHeight: 0, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", overflow: "auto" }}>
                    <Box sx={{ display: "grid", 

/*                       gridTemplateColumns: "0.9fr 0.7fr 0.9fr 1fr 0.9fr 1fr", 
                      minWidth: 720  */

                      gridTemplateColumns: "0.9fr 0.7fr 0.9fr 1fr 0.9fr 1fr 0.9fr",
                      minWidth: 840

                      }}>
                      <HeaderCell
                        onClick={() => handleSort(setTruckSort, "truck")}
                        active={truckSort.field === "truck"}
                        direction={truckSort.direction}
                      >
                        Truck
                      </HeaderCell>

                      <HeaderCell
                        align="center"
                        onClick={() => handleSort(setTruckSort, "incidents")}
                        active={truckSort.field === "incidents"}
                        direction={truckSort.direction}
                      >
                        #Incidents
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setTruckSort, "avgSeconds")}
                        active={truckSort.field === "avgSeconds"}
                        direction={truckSort.direction}
                      >
                        Avg Duration
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setTruckSort, "totalSeconds")}
                        active={truckSort.field === "totalSeconds"}
                        direction={truckSort.direction}
                      >
                        Total Duration
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setTruckSort, "totalFuel")}
                        active={truckSort.field === "totalFuel"}
                        direction={truckSort.direction}
                      >
                        Total Fuel
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setTruckSort, "totalCost")}
                        active={truckSort.field === "totalCost"}
                        direction={truckSort.direction}
                      >
                        Approx Idle Cost
                      </HeaderCell>

                      <HeaderCell
                        onClick={() => handleSort(setTruckSort, "approxPpg")}
                        active={truckSort.field === "approxPpg"}
                        direction={truckSort.direction}
                      >
                        Approx PPG
                      </HeaderCell>

                      {sortedTruckRows.map((row) => (
                        <React.Fragment key={row.truck}>
                          <Cell
                            bg={filters.truck === row.truck ? "#bbdefb" : "#f3f3f3"}
                          >
                            <Box
                              onClick={() => toggleFilter("truck", row.truck)}
                              sx={{ cursor: "pointer", fontWeight: 800 }}
                            >
                              {row.truck}
                            </Box>
                          </Cell>
                          <Cell align="center">{n0.format(row.incidents)}</Cell>
                          <Cell bg={getDurationColor(row.avgSeconds)}>{formatHoursMinutes(row.avgSeconds)}</Cell>
                          <Cell>{formatDurationLong(row.totalSeconds)}</Cell>
                          <Cell>{n1.format(row.totalFuel)} g</Cell>
                          <Cell bg={getCostColor(row.totalCost)}>{money.format(row.totalCost)}</Cell>
                          <Cell>{money.format(row.approxPpg)}</Cell>
                        </React.Fragment>
                      ))}

                      <Cell bold>Total</Cell>
                      <Cell align="center" bold>{n0.format(truckTable.total.incidents)}</Cell>
                      <Cell bg={getDurationColor(truckTable.total.avgSeconds)} bold>{formatHoursMinutes(truckTable.total.avgSeconds)}</Cell>
                      <Cell bold>{formatDurationLong(truckTable.total.totalSeconds)}</Cell>
                      <Cell bold>{n1.format(truckTable.total.totalFuel)} g</Cell>
                      <Cell bg={getCostColor(truckTable.total.totalCost)} bold>{money.format(truckTable.total.totalCost)}</Cell>
                      <Cell bold>{money.format(truckTable.total.approxPpg)}</Cell>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
